"use client"

import { createContext, useContext, useState, useCallback } from "react"
import type { ReactNode } from "react"

const DEFAULT_LOGO = "/images/design-mode/logo(1).svg"
const DEFAULT_NAME = "Versafy"

interface PlatformBrandingContextValue {
  logoUrl: string
  /** The square platform mark (browser tab icon); null until one is uploaded. */
  faviconUrl: string | null
  isCustom: boolean
  platformName: string
  refreshLogo: () => Promise<void>
}

const PlatformBrandingContext = createContext<PlatformBrandingContextValue>({
  logoUrl: DEFAULT_LOGO,
  faviconUrl: null,
  isCustom: false,
  platformName: DEFAULT_NAME,
  refreshLogo: async () => {},
})

export function PlatformLogoProvider({
  initialLogoUrl,
  initialFaviconUrl = null,
  initialPlatformName,
  children,
}: {
  initialLogoUrl: string
  initialFaviconUrl?: string | null
  initialPlatformName: string
  children: ReactNode
}) {
  const [logoUrl, setLogoUrl] = useState(initialLogoUrl)

  const refreshLogo = useCallback(async () => {
    try {
      const res = await fetch("/api/system/logo")
      if (!res.ok) return
      const { url } = await res.json()
      setLogoUrl(url || DEFAULT_LOGO)
    } catch {
      // keep current
    }
  }, [])

  const isCustom = logoUrl !== DEFAULT_LOGO

  return (
    <PlatformBrandingContext.Provider
      value={{
        logoUrl,
        faviconUrl: initialFaviconUrl,
        isCustom,
        platformName: initialPlatformName,
        refreshLogo,
      }}
    >
      {children}
    </PlatformBrandingContext.Provider>
  )
}

export function usePlatformLogo() {
  return useContext(PlatformBrandingContext)
}
